import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BlogService from '../../services/blogService';
import './Blog.css';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState(null);

  const categories = ['all', 'Career Advice', 'Resume Tips', 'Interview Tips', 'Industry Trends', 'Networking', 'Salary & Benefits'];

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const data = await BlogService.getAllBlogs(currentPage, 9);
        setBlogs(data.content || []);
        setTotalPages(data.totalPages || 0);
        setError(null);
      } catch (error) {
        console.error('Error fetching blogs:', error);
        setError('Failed to fetch blogs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [currentPage]);

  // Filter posts based on search and category
  const filteredPosts = blogs.filter(post => {
    const title = post.title || '';
    const content = post.content || '';
    const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all'; // You can implement category filtering based on API response
    return matchesSearch && matchesCategory;
  });

  const postsPerPage = 9;
  const indexOfLastPost = (currentPage + 1) * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);

  // For now, we'll use the first 3 posts as featured (you can modify based on API response)
  const featuredPosts = blogs.slice(0, 3);

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber - 1); // API uses 0-based indexing
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to create a slug from title
  const createSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/--+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Function to strip HTML tags
  const stripHtml = (html) => {
    return html.replace(/<\/?[^>]+(>|$)/g, "");
  };

  // Function to truncate text
  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  return (
    <div className="blog-page">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <div className="blog-hero-content">
            <h1>Latest Updates</h1>
            <p>Expert insights, career advice, and industry trends to help you navigate your professional journey</p>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="blogPage_section">
        <div className="container">
          {/* Blog Posts Row */}
          <div className="row blog-posts-row">
            {loading ? (
              <div className="col-12 col-md-12">
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading articles, please wait...</p>
                </div>
              </div>
            ) : error ? (
              <div className="col-12 col-md-12">
                <div className="error-container">
                  <div className="error-icon">
                    <i className="fas fa-exclamation-circle"></i>
                  </div>
                  <h3>Unable to Load Articles</h3>
                  <p>{error}</p>
                </div>
              </div>
            ) : filteredPosts.length > 0 ? (
              filteredPosts.map(post => (
                <div className="blog-card-col" key={post.id}>
                  <article className="blog_content border-0">
                    <div className="b-image-wrapper">
                      <img
                        src={post.coverImageUrl || '/images/title-bg.jpg'}
                        alt={post.coverImageAlt || post.title}
                        className="img-fluid b-image"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/title-bg.jpg';
                        }}
                      />
                    </div>
                    <div className="b-card-body">
                      <span className="b-date">
                        <i className="far fa-calendar" title="date"></i>
                        {new Date(post.publishedDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: '2-digit',
                          year: 'numeric'
                        })}
                      </span>
                      <h2 className="b-title">
                        <Link to={`/blog/${post.formattedTitle || createSlug(post.title)}`} title={post.title}>
                          {post.title}
                        </Link>
                      </h2>
                      <p className="b-description">
                        {truncateText(stripHtml(post.content), 100)}
                      </p>
                      <div className="b-card-footer">
                        <Link className="b-btn" to={`/blog/${post.formattedTitle || createSlug(post.title)}`}>
                          <span>Read More</span>
                          <i className="fas fa-arrow-right b-btn-icon"></i>
                        </Link>
                      </div>
                    </div>
                  </article>
                </div>
              ))
            ) : (
              <div className="col-12 col-md-12">
                <div className="no-posts">
                  <div className="no-posts-icon">
                    <i className="fas fa-newspaper"></i>
                  </div>
                  <h3>No articles found</h3>
                  <p>Try adjusting your search or browse different categories.</p>
                </div>
              </div>
            )}
          </div>

          {/* Pagination Row */}
          {!loading && !error && filteredPosts.length > 0 && totalPages > 1 && (
            <div className="row">
              <div className="col-12 col-md-12">
                <div className="blog-pagination">
                  <button
                    className="btn btn-secondary blog-nav-btn prev-btn"
                    disabled={currentPage === 0}
                    onClick={handlePreviousPage}
                    aria-label="Previous Page"
                  >
                    <i className="fas fa-chevron-left"></i>
                    <span>Previous</span>
                  </button>
                  <span className="blog-page-indicator">
                    Page <strong>{currentPage + 1}</strong> of <strong>{totalPages}</strong>
                  </span>
                  <button
                    className="btn btn-secondary blog-nav-btn next-btn"
                    disabled={currentPage >= totalPages - 1}
                    onClick={handleNextPage}
                    aria-label="Next Page"
                  >
                    <span>Next</span>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
